import withParams from "@src/components/widgets/HOC/withParams";
import React, { useCallback, useMemo } from "react";
import { WidgetTemplateComponentType } from "@src/interface/Widget";
import { ILandingStandardWidgetHighlightTwoProductsPetitPrinceParams } from "../interface";
import { useTranslation } from "@src/hooks/useTranslation";
import useCtaAction from "@src/components/widgets/hooks/useCtaAction";
import useBackboneContext from "@src/backbone/provider/useBackboneContext";
import { useAllProduct } from "@src/hooks/query/useAllProduct";
import LandingHighlightTwoProducts, {
  ILandingHighlightTwoProductsProduct,
} from "design-system/src/Components/WidgetPart/LandingHighlightTwoProducts/LandingHighlightTwoProducts";
import HighlightProductCard, {
  IHighlightProductCardProps,
} from "design-system/src/Components/WidgetPart/LandingHighlightTwoProducts/HighlightProductCard/HighlightProductCard";
import { ECtaAction } from "@src/components/widgets/hooks/useCtaAction";

interface ProductAdditionalData {
  actionType?: ECtaAction;
  link?: string;
}

const ProductItemRender = (
  props: IHighlightProductCardProps<ProductAdditionalData>
) => {
  const { product: flatProduct, onClick, ...rest } = props;
  const { id_product } = flatProduct;
  const { t } = useTranslation(["landing"]);
  const { generateClick, getSubmitLabel } = useCtaAction(id_product || "");

  // Type assertion to access additional data
  const productWithData =
    flatProduct as ILandingHighlightTwoProductsProduct<ProductAdditionalData>;
  const actionType = productWithData.actionType;
  const link = productWithData.link;

  return (
    <HighlightProductCard
      {...rest}
      product={{
        ...flatProduct,
        ctaLabel: getSubmitLabel(false, t("Je commande", "landing")),
      }}
      onClick={generateClick(
        actionType && actionType === "open_link"
          ? { type: "open_link", link: link || "#" }
          : { type: actionType || "product_card" }
      )}
    />
  );
};

const HighlightTwoProductsPetitPrince: WidgetTemplateComponentType =
  withParams<ILandingStandardWidgetHighlightTwoProductsPetitPrinceParams>(
    (props) => {
      const { params } = props;
      const { app } = useBackboneContext();
      const { data: products } = useAllProduct();

      const prepareProducts = useMemo(() => {
        const _products: ILandingHighlightTwoProductsProduct<ProductAdditionalData>[] =
          [];
        const _params: Record<string, string | undefined> = params as any;

        // Process both products
        [1, 2].forEach((index) => {
          const id_product = _params[`id_product_${index}`];
          const image_product = _params[`product_${index}_image`];
          const displayFromMentionRaw =
            _params[`product_${index}_display_from_mention`];
          const actionType = _params[`action_product_${index}`] as any;
          const link = _params[`link_product_${index}`];

          if (id_product) {
            const productModel = app.getProduct(id_product);
            const productData = products.find(
              (product) =>
                !!(product.id_product && product.id_product === id_product)
            );

            _products.push({
              id_product: id_product,
              name: productModel?.get("name") || productData?.name || "",
              price: productModel?.formatPrice() || "",
              imageId: image_product || "-1",
              displayFromMention:
                displayFromMentionRaw === "1" ? "À partir de" : undefined,
              actionType: actionType || "product_card",
              link,
              decorationVariant: index === 1 ? "star" : "fox",
            });
          }
        });

        return _products;
      }, [params, app, products]);

      const renderProduct = useCallback(
        (
          data: IHighlightProductCardProps<ProductAdditionalData>,
          index: number
        ) => {
          return (
            <ProductItemRender
              key={data.product.id_product + "-" + index}
              {...data}
            />
          );
        },
        []
      );

      const _params: Record<string, string | undefined> = params as any;
      const title = _params.title;
      const subtitle = _params.subtitle;
      const description = _params.description;

      return (
        <LandingHighlightTwoProducts<ProductAdditionalData>
          title={title}
          subtitle={subtitle}
          description={description}
          products={prepareProducts}
          renderProduct={renderProduct}
        />
      );
    }
  );

export default HighlightTwoProductsPetitPrince;
